import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { Search, X, Table, BarChart, BarChart2, PieChart, LineChart, FileText, Pin, PinOff, Trash2, Edit2, Check, X as XIcon, CalendarClock } from 'lucide-react';
import useQueryLibrary from '../../hooks/useQueryLibrary';
import { useAuth } from '../../contexts/AuthContext';


const QueryLibrary = forwardRef(({ 
  onQuerySelect, 
  isVisible, 
  onClose, 
  hasQueryResult = false, 
  mobileLibraryOpened = false, 
  onMobileLibraryOpenedChange,
  getQuerySettings: propGetQuerySettings
}, ref) => {
  const {
    queries,
    isLoading,
    error,
    lastFetchTime,
    fetchSavedQueries,
    filteredQueries: getFilteredQueries,
    togglePinQuery,
    pinnedQueries,
    getSortedQueries,
    MAX_PINNED_QUERIES,
    hideQuery,
    getQuerySettings: hookGetQuerySettings,
    saveQueryTitle
  } = useQueryLibrary();
  
  const getQuerySettings = propGetQuerySettings || hookGetQuerySettings;
  
  const [searchQuery, setSearchQuery] = useState('');
  const [hoverCardId, setHoverCardId] = useState(null);
  const [newQueryId, setNewQueryId] = useState(null);
  const [previousQueriesIds, setPreviousQueriesIds] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingQueryId, setEditingQueryId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const libraryRef = useRef(null);
  const editInputRef = useRef(null);
  const { currentUser } = useAuth();

  
  // Add refresh function
  const refreshLibrary = useCallback(() => {
    // Save current query IDs before refreshing
    setPreviousQueriesIds(queries.map(q => q.id));
    fetchSavedQueries(true);
  }, [fetchSavedQueries, queries]);
  
  // Expose the refresh function through the ref
  useImperativeHandle(ref, () => ({
    refresh: refreshLibrary,
    collapse: () => setIsExpanded(false),
    expand: () => setIsExpanded(true)
  }));
  
  // When queries change after a refresh, detect new query
  useEffect(() => {
    if (previousQueriesIds.length > 0 && queries.length > 0) {
      // Find any new query IDs that weren't in the previous set
      const newIds = queries
        .map(q => q.id)
        .filter(id => !previousQueriesIds.includes(id));
      
      if (newIds.length > 0) {
        // Get the most recently added query (usually the first one in sorted order)
        setNewQueryId(newIds[0]);
        
        // Clear the animation after a few seconds
        const timer = setTimeout(() => {
          setNewQueryId(null);
          setPreviousQueriesIds([]);
        }, 10000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [queries, previousQueriesIds]);
  
 // Refresh data when component mounts or becomes visible
useEffect(() => {
  if (isVisible) {
    const now = Date.now();
    const shouldRefresh = now - lastFetchTime > 5 * 60 * 1000; // 5 minutes in milliseconds
    fetchSavedQueries(shouldRefresh);
  }
}, [isVisible]); // Remove fetchSavedQueries and lastFetchTime from dependencies

  useEffect(() => {
    if (mobileLibraryOpened) {
      setIsExpanded(true);
    } else if (hasQueryResult) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  }, [hasQueryResult, mobileLibraryOpened]);

  const handleRunQuery = (query) => {
    if (onQuerySelect) {
      onQuerySelect(query);
    }
  };

  const handlePinToggle = (e, queryId) => {
    e.stopPropagation(); // Prevent triggering the parent button click
    togglePinQuery(queryId);
  };

  const handleHideQuery = (e, query) => {
    e.stopPropagation(); // Prevent triggering the parent button click
    if (window.confirm(`Hide "${query.name || 'this query'}" from your library?`)) {
      hideQuery(query.id);
    }
  };

  const getQueryIcon = (query) => {
    // Get saved visualization settings for this query
    const querySettings = getQuerySettings()[query.id] || {};
    
    // Use either the saved settings from localStorage or the settings stored with the query
    const effectiveSettings = {
      ...query.cardSettings,  // Settings saved in Firestore with the query
      ...querySettings        // Override with settings from localStorage (has higher priority)
    };
    
    const viewMode = effectiveSettings.viewMode || (query.cardSettings ? query.cardSettings.viewMode : 'table');
    const chartType = effectiveSettings.chartType || (query.cardSettings ? query.cardSettings.chartType : 'bar');
    
    // Check if it's a single value result
    const isSingleValue = query.result && query.result.length === 1 && 
                         Object.keys(query.result[0]).length === 1;
    
    if (isSingleValue) {
      return <FileText className="h-4 w-4 text-gray-500" />;
    }
    
    if (viewMode === 'table') {
      return <Table className="h-4 w-4 text-gray-500" />;
    }
    
    // Chart icons based on chart type or view mode
    switch (chartType || viewMode) {
      case 'bar':
        return <BarChart className="h-4 w-4 text-gray-500" />;
      case 'line':
        return <LineChart className="h-4 w-4 text-gray-500" />;
      case 'pie':
        return <PieChart className="h-4 w-4 text-gray-500" />;
      case 'scatter':
        return <BarChart2 className="h-4 w-4 text-gray-500" />;
      default:
        return <BarChart className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get proper query name
  const getQueryName = (query) => {
    // First check if there is a user-specific custom title
    if (query.userCustomTitles && query.userCustomTitles[currentUser?.email]) {
      return query.userCustomTitles[currentUser?.email];
    }
    
    // Fall back to standard title detection
    return (query.explanation && query.explanation.title) || 
           query.name || 
           (query.userQuery ? query.userQuery : 'Unnamed Query');
  };

  // Get filtered queries using the hook's filter function
  const filteredQueries = useMemo(() => {
    const filtered = getFilteredQueries(searchQuery);
    return getSortedQueries(filtered);
  }, [getFilteredQueries, getSortedQueries, searchQuery]);

  // Skip rendering if not visible
  if (!isVisible) {
    return null;
  }

// Handle mouse enter/leave events for hover expansion
const handleMouseEnter = () => {
  setIsExpanded(true);
};

const handleMouseLeave = () => {
  if (hasQueryResult && !mobileLibraryOpened) {
    setIsExpanded(false);
  }
  // Clear mobile opened state when mouse leaves (desktop behavior)
  if (mobileLibraryOpened && onMobileLibraryOpenedChange && window.innerWidth >= 768) {
    onMobileLibraryOpenedChange(false);
  }
};

  // Render placeholder items that match the real queries in style
  const renderPlaceholders = (count = 5, lowOpacity = false) => {
    // Return array of placeholder cards with white background
    return Array.from({ length: count }).map((_, index) => {
      return (
        <div 
          key={`placeholder-${lowOpacity ? 'subtle-' : ''}${index}`}
          className={`w-full rounded-lg p-2.5 border border-gray-100 
                     bg-white
                     ${lowOpacity ? 'opacity-30' : ''}`}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <div className="bg-gray-200 p-1 rounded-full h-6 w-6"></div>
            <div className="h-4 bg-gray-200 rounded w-16"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded mb-2 w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      );
    });
  };

  // Check if we should show the search box
  const shouldShowSearch = getFilteredQueries('').length > 5;
  
  // Determine the queries to display based on search (even in collapsed state)
  const queriesToDisplay = filteredQueries;

  const handleStartEdit = (e, query) => {
    e.stopPropagation(); // Prevent card click
    setEditingQueryId(query.id);
    
    // Use custom title if it exists, otherwise use the query name
    const currentTitle = 
      (query.userCustomTitles && query.userCustomTitles[currentUser?.email]) || 
      getQueryName(query);
    
    setEditTitle(currentTitle);
    
    // Focus the input and auto-resize after a short delay (to ensure it exists in the DOM)
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
        editInputRef.current.select();
        // Auto-resize to fit content with proper calculation
        editInputRef.current.style.height = 'auto';
        editInputRef.current.style.height = (editInputRef.current.scrollHeight + 2) + 'px';
      }
    }, 100);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation(); // Prevent card click
    setEditingQueryId(null);
    setEditTitle('');
  };

  const handleSaveTitle = async (e, queryId) => {
    e.stopPropagation(); // Prevent card click
    
    if (editTitle.trim() === '') return;
    
    // Save the new title
    await saveQueryTitle(queryId, editTitle);
    
    // Exit edit mode
    setEditingQueryId(null);
    setEditTitle('');
  };

  const handleTitleKeyDown = (e, queryId) => {
    // Always stop propagation to prevent triggering the parent button
    e.stopPropagation();
    
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default button behavior
      handleSaveTitle(e, queryId);
    } else if (e.key === 'Escape') {
      handleCancelEdit(e);
    }
    // Remove the space key handling - let it type normally
  };

  return (
    <div 
      ref={libraryRef}
      className={`h-full flex flex-col overflow-hidden shadow-sm border-r border-gray-100
                  transition-all duration-300 ease-in-out ${
        isExpanded ? 'w-[220px]' : 'w-[60px]'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Close button - visible on mobile in expanded mode */}
      <div className={`p-2 flex justify-end ${isExpanded ? 'visible' : 'invisible'} md:hidden`}>
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Close library"
        >
          <X size={16} />
        </button>
      </div>
      
      {/* Search box - always present but visually hidden when collapsed */}
      {shouldShowSearch && (
        <div className={`px-4 py-2 ${isExpanded ? '' : 'opacity-0 absolute pointer-events-none'}`}>
          <div className="relative flex items-center">
            <Search className="absolute left-0 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-gray-500 placeholder-gray-500 bg-transparent 
                        border-0 border-b border-purple-200 focus:border-purple-300
                        focus:outline-none text-sm"
            />
          </div>
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto scroll-container p-2">
        {isLoading && queries.length === 0 ? (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          </div>
        ) : error && queries.length === 0 ? (
          <div className={`text-red-500 py-2 text-center text-xs ${!isExpanded && 'px-1'}`}>
            {isExpanded ? error : '!'}
          </div>
        ) : queriesToDisplay.length === 0 ? (
          isExpanded ? (
            <div className="grid gap-3">
              {searchQuery ? (
                <div className="text-gray-500 p-2 text-center text-sm">No matches</div>
              ) : (
                <>
                  {/* Add "Saved Queries" text at the top with left alignment */}
                  <div className="p-4 text-left text-gray-500 text-sm flex flex-col items-start">
                    <span>Saved Queries</span>
                  </div>
                  {/* Show placeholder cards */}
                  {renderPlaceholders()}
                </>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-4">
              {/* Empty state in collapsed view - intentionally empty */}
            </div>
          )
        ) : (
          <div className="grid gap-2">
            {/* Actual queries */}
            {queriesToDisplay.map(query => {
              const isPinned = pinnedQueries.includes(query.id);
              const isNew = query.id === newQueryId;
              const queryName = getQueryName(query);
              
              return (
                <button
                  key={query.id}
                  onClick={() => {
                    // Don't run query if we're in edit mode
                    if (editingQueryId !== query.id) {
                      handleRunQuery(query);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent space from activating button when editing
                    if (editingQueryId === query.id && e.key === ' ') {
                      e.preventDefault();
                    }
                  }}
                  onMouseEnter={() => setHoverCardId(query.id)}
                  onMouseLeave={() => setHoverCardId(null)}
                  className={`w-full text-left transition-all rounded-lg
                             border bg-white relative
                             ${isExpanded 
                               ? 'p-2.5' 
                               : 'p-2 flex justify-center'
                             }
                             ${isNew 
                               ? 'animate-pulse border-secondary shadow-md' 
                               : 'border-gray-100 hover:bg-gray-50'}
                             ${isPinned ? 'bg-primary/5' : ''}`}
                  style={{
                    boxShadow: isNew ? '0 0 10px rgba(255, 173, 255, 0.5)' : ''
                  }}
                >
                  {isExpanded ? (
                    <>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className={`p-1 rounded-full ${isNew ? 'bg-secondary/20' : 'bg-primary/10'}`}>
                            {getQueryIcon(query)}
                          </div>
                          {!query.isPrivate && (
                            <span className="px-1.5 py-0.5 text-[10px] font-normal rounded-full bg-blue-100/70 text-blue-700">
                              Shared
                            </span>
                          )}
                          {query.scheduledQueryId && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-normal rounded-full bg-green-100/70 text-green-700" title="Scheduled">
                              <CalendarClock size={10} />
                              <span>Scheduled</span>
                            </span>
                          )}
                          {isNew && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-secondary/20 text-secondary animate-pulse">
                              New
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div 
                            className={`transition-opacity duration-150 
                                      ${hoverCardId === query.id ? 'opacity-100' : 'opacity-0'}`}
                          >
                            <button 
                              onClick={(e) => handleHideQuery(e, query)}
                              className="text-gray-400 hover:text-red-500"
                              title="Hide query from library"
                              aria-label="Hide query from library"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <div 
                            className={`transition-opacity duration-150 
                                      ${(hoverCardId === query.id || isPinned) ? 'opacity-100' : 'opacity-0'}`}
                          >
                            {isPinned ? (
                              <button 
                                onClick={(e) => handlePinToggle(e, query.id)}
                                className="text-primary hover:text-primary/80 relative group"
                                title="Unpin query"
                                aria-label="Unpin query"
                              >
                                <Pin size={14} className="group-hover:opacity-0" />
                                <PinOff size={14} className="absolute top-0 left-0 opacity-0 group-hover:opacity-100" />
                              </button>
                            ) : (
                              <button 
                                onClick={(e) => handlePinToggle(e, query.id)}
                                className="text-gray-400 hover:text-primary"
                                title={pinnedQueries.length >= MAX_PINNED_QUERIES 
                                  ? `Maximum ${MAX_PINNED_QUERIES} pins reached. Unpin others first.` 
                                  : "Pin query to top"}
                                aria-label="Pin query to top"
                                disabled={pinnedQueries.length >= MAX_PINNED_QUERIES}
                              >
                                <Pin size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {editingQueryId === query.id ? (
                        <div onClick={(e) => e.stopPropagation()} className="space-y-1">
                          <textarea
                            ref={editInputRef}
                            value={editTitle}
                            onChange={(e) => {
                              e.stopPropagation();
                              setEditTitle(e.target.value);
                              // Auto-resize the textarea with a small delay to ensure proper calculation
                              setTimeout(() => {
                                if (e.target) {
                                  e.target.style.height = 'auto';
                                  e.target.style.height = (e.target.scrollHeight + 2) + 'px';
                                }
                              }, 0);
                            }}
                            onKeyDown={(e) => {
                              // Prevent Enter from creating a new line
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                              handleTitleKeyDown(e, query.id);
                            }}
                            onKeyUp={(e) => e.stopPropagation()}
                            onKeyPress={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full text-sm border border-purple-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary resize-none overflow-hidden leading-normal"
                            rows={1}
                            style={{ minHeight: '32px', lineHeight: '1.5' }}
                          />
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={(e) => handleSaveTitle(e, query.id)}
                              className="text-green-500 hover:text-green-600 px-2 py-0.5 text-xs bg-green-50 rounded hover:bg-green-100"
                              title="Save"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-gray-500 hover:text-gray-700 px-2 py-0.5 text-xs bg-gray-50 rounded hover:bg-gray-100"
                              title="Cancel"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                      <div className="group flex items-center gap-1">
                        <h3 className="text-sm font-normal text-gray-700 line-clamp-2 flex-1">
                          {(query.userCustomTitles && query.userCustomTitles[currentUser?.email]) || queryName}
                        </h3>
                        <button
                          onClick={(e) => handleStartEdit(e, query)}
                          className="text-gray-400 hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                          title="Edit title"
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>
                    )}
                    </>
                  ) : (
                    // Without any pin indicator
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isNew ? 'bg-secondary/20' : 'bg-primary/10'}`}>
                      {getQueryIcon(query)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
        
        {/* Add a subtle loading indicator while refreshing in background */}
        {isLoading && queries.length > 0 && (
          <div className="text-center py-2">
            <div className="inline-block w-4 h-4 border-b-2 border-primary rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    </div>
  );
});

export default QueryLibrary;